// src/app/settings/application/work-orders/assign-work-orders/assign-work-orders.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';

import {
  UsersResponse,
  UserRow,
  DepartmentsResponse,
  DepartmentRow,
  WorkOrderNumbersResponse,
  WorkOrderNumberRow,
  AssignUserWonPayload,
  AssignResult,
} from '../models/work-orders-assign.model';

@Injectable({ providedIn: 'root' })
export class AssignWorkOrdersService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  // ---------------------------------------
  // Users (paginated) - filters to Active + has employeeNumber + not deleted
  // ---------------------------------------
getUsers(
  pageNumber: number = 1,
  pageSize: number = 100,
): Observable<UsersResponse | null> {
  const params = new HttpParams()
    .set('pageNumber', String(pageNumber))
    .set('pageSize', String(pageSize))
    .set('employmentStatus', 'Active');

  return this.http.get<UsersResponse>(`${this.baseUrl}/api/users`, { params }).pipe(
    map((res) => ({
      ...res,
      data: (res.data || []).filter((u) => this.isAssignableEmployee(u)),
    })),
    catchError((err) => {
      console.error('Error fetching users:', err);
      return of(null);
    }),
  );
}

  // ---------------------------------------
  // Departments (paginated)
  // ---------------------------------------
  getDepartments(
    pageNumber: number = 1,
    pageSize: number = 20,
    orderBy: string = 'codeAsc',
  ): Observable<DepartmentsResponse | null> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('orderBy', orderBy);

    return this.http.get<DepartmentsResponse>(`${this.baseUrl}/api/departments`, { params }).pipe(
      catchError((err) => {
        console.error('Error fetching departments:', err);
        return of(null);
      }),
    );
  }

  // ---------------------------------------
  // Work Orders (paginated)
  // ---------------------------------------
  getWorkOrderNumbers(
    pageNumber: number = 1,
    pageSize: number = 20,
  ): Observable<WorkOrderNumbersResponse | null> {
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);

    return this.http
      .get<WorkOrderNumbersResponse>(`${this.baseUrl}/api/work-order-numbers`, { params })
      .pipe(
        catchError((err) => {
          console.error('Error fetching work order numbers:', err);
          return of(null);
        }),
      );
  }

  // ---------------------------------------
  // Assign WON to a single user (single call)
  // Backend handles duplicates and returns 400 with message:
  // "User Work Order Number association already exists."
  // ---------------------------------------
  assignUserWon(employeeId: number, workOrderNumberId: number): Observable<AssignResult> {
    const payload: AssignUserWonPayload = { employeeId, workOrderNumberId };

    return this.http.post(`${this.baseUrl}/api/user-wons`, payload).pipe(
      map(() => ({ ok: true } as AssignResult)),
      catchError((err: HttpErrorResponse) => {
        const message =
          typeof err?.error === 'string'
            ? err.error
            : err?.error?.message || err?.message || 'Request failed';

        const result: AssignResult = {
          ok: false,
          statusCode: err.status,
          message,
          rawError: err,
        };

        return of(result);
      }),
    );
  }

  // ---------------------------------------
  // Helpers
  // ---------------------------------------
  private isAssignableEmployee(u: UserRow): boolean {
    const hasEmployeeNumber = !!(u.employeeNumber || '').trim();
    const isActive = (u.employeeStatus || '').toLowerCase() === 'active';
    const notDeleted = !u.isDeleted;

    return hasEmployeeNumber && isActive && notDeleted;
  }
}
